import React, { useState, useEffect } from 'react';
import { Plus, Pencil, Trash2, Loader2, Trophy, Save, X, Star, FolderOpen } from 'lucide-react';
import { achievementsAPI } from '../../services/api';
import { useLanguage } from '../../contexts/LanguageContext';
import { toast } from 'sonner';
import ImageUploader from '../../components/ImageUploader';

const AdminAchievements = () => {
  const { t, language } = useLanguage();
  const [achievements, setAchievements] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingAchievement, setEditingAchievement] = useState(null);
  const [editingCategory, setEditingCategory] = useState(null);
  const [showAchievementForm, setShowAchievementForm] = useState(false);
  const [showCategoryForm, setShowCategoryForm] = useState(false);
  const [activeTab, setActiveTab] = useState('achievements'); // 'achievements' or 'categories'
  const [langTab, setLangTab] = useState('en'); // 'en' or 'tr'
  const [saving, setSaving] = useState(false);
  
  // Form states
  const [achievementForm, setAchievementForm] = useState({
    title: '', title_tr: '',
    description: '', description_tr: '',
    year: new Date().getFullYear(),
    ranking: '', ranking_tr: '',
    location: '', location_tr: '',
    image: '',
    category_id: '',
    featured: false,
    featured_color: 'yellow',
    order: 0
  });
  
  const [categoryForm, setCategoryForm] = useState({
    name: '', name_tr: '',
    icon: 'trophy',
    order: 0
  });

  const iconOptions = [
    { value: 'trophy', label: 'Trophy' },
    { value: 'plane', label: 'Plane' },
    { value: 'rocket', label: 'Rocket' },
    { value: 'flask', label: 'Flask' },
    { value: 'bot', label: 'Robot' },
    { value: 'award', label: 'Award' }
  ];

  const colorOptions = [
    { value: 'yellow', label: language === 'tr' ? 'Sarı' : 'Yellow', color: '#FFD700' },
    { value: 'red', label: language === 'tr' ? 'Kırmızı' : 'Red', color: '#EF4444' },
    { value: 'blue', label: language === 'tr' ? 'Mavi' : 'Blue', color: '#00F0FF' },
    { value: 'green', label: language === 'tr' ? 'Yeşil' : 'Green', color: '#22C55E' }
  ];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [achievementsData, categoriesData] = await Promise.all([
        achievementsAPI.getAll(),
        achievementsAPI.getCategories()
      ]);
      setAchievements(achievementsData);
      setCategories(categoriesData);
    } catch (err) {
      toast.error(t('admin.toast.error'));
    } finally {
      setLoading(false);
    }
  };

  // Achievement handlers
  const handleAchievementSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingAchievement) {
        await achievementsAPI.update(editingAchievement.id, achievementForm);
        toast.success(t('admin.toast.updated'));
      } else {
        await achievementsAPI.create(achievementForm);
        toast.success(t('admin.toast.saved'));
      }
      resetAchievementForm();
      fetchData();
    } catch (err) {
      toast.error(err.message || t('admin.toast.error'));
    } finally {
      setSaving(false);
    }
  };

  const handleEditAchievement = (achievement) => {
    setEditingAchievement(achievement);
    setAchievementForm({
      title: achievement.title || '',
      title_tr: achievement.title_tr || '',
      description: achievement.description || '',
      description_tr: achievement.description_tr || '',
      year: achievement.year || new Date().getFullYear(),
      ranking: achievement.ranking || '',
      ranking_tr: achievement.ranking_tr || '',
      location: achievement.location || '',
      location_tr: achievement.location_tr || '',
      image: achievement.image || '',
      category_id: achievement.category_id || '',
      featured: achievement.featured || false,
      featured_color: achievement.featured_color || 'yellow',
      order: achievement.order || 0
    });
    setShowAchievementForm(true);
  };

  const handleDeleteAchievement = async (id) => {
    if (!window.confirm(t('admin.common.confirmDelete'))) return;
    try {
      await achievementsAPI.delete(id);
      toast.success(t('admin.toast.deleted'));
      fetchData();
    } catch (err) {
      toast.error(err.message || t('admin.toast.error'));
    }
  };

  const resetAchievementForm = () => {
    setEditingAchievement(null);
    setAchievementForm({
      title: '', title_tr: '',
      description: '', description_tr: '',
      year: new Date().getFullYear(),
      ranking: '', ranking_tr: '',
      location: '', location_tr: '',
      image: '',
      category_id: categories[0]?.id || '',
      featured: false,
      featured_color: 'yellow',
      order: 0
    });
    setShowAchievementForm(false);
    setLangTab('en');
  };

  // Category handlers
  const handleCategorySubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingCategory) {
        await achievementsAPI.updateCategory(editingCategory.id, categoryForm);
        toast.success(t('admin.toast.updated'));
      } else {
        await achievementsAPI.createCategory(categoryForm);
        toast.success(t('admin.toast.saved'));
      }
      resetCategoryForm();
      fetchData();
    } catch (err) {
      toast.error(err.message || t('admin.toast.error'));
    } finally {
      setSaving(false);
    }
  };

  const handleEditCategory = (category) => {
    setEditingCategory(category);
    setCategoryForm({
      name: category.name || '',
      name_tr: category.name_tr || '',
      icon: category.icon || 'trophy',
      order: category.order || 0
    });
    setShowCategoryForm(true);
  };

  const handleDeleteCategory = async (id) => {
    if (!window.confirm(t('admin.common.confirmDelete'))) return;
    try {
      await achievementsAPI.deleteCategory(id);
      toast.success(t('admin.toast.deleted'));
      fetchData();
    } catch (err) {
      toast.error(err.message || t('admin.achievements.categories.cannotDelete'));
    }
  };

  const resetCategoryForm = () => {
    setEditingCategory(null);
    setCategoryForm({
      name: '', name_tr: '',
      icon: 'trophy',
      order: 0
    });
    setShowCategoryForm(false);
    setLangTab('en');
  };

  const getCategoryName = (categoryId) => {
    const category = categories.find(c => c.id === categoryId);
    if (!category) return '-';
    return language === 'tr' && category.name_tr ? category.name_tr : category.name;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-cyan-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-achievements">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">{t('admin.achievements.title')}</h1>
          <p className="text-gray-400 mt-1">{t('admin.achievements.description')}</p>
        </div>
      </div>

      {/* Tab Buttons */}
      <div className="flex gap-4 border-b border-gray-700 pb-4">
        <button
          onClick={() => setActiveTab('achievements')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
            activeTab === 'achievements'
              ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/50'
              : 'text-gray-400 hover:text-white'
          }`}
        >
          <Trophy size={18} />
          {t('admin.achievements.title')} ({achievements.length})
        </button>
        <button
          onClick={() => setActiveTab('categories')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
            activeTab === 'categories'
              ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/50'
              : 'text-gray-400 hover:text-white'
          }`}
        >
          <FolderOpen size={18} />
          {t('admin.achievements.categories.title')} ({categories.length})
        </button>
      </div>

      {/* Achievements Tab */}
      {activeTab === 'achievements' && (
        <div className="space-y-6">
          {/* Add Button */}
          <button
            onClick={() => {
              resetAchievementForm();
              setShowAchievementForm(true);
            }}
            className="flex items-center gap-2 px-4 py-2 bg-cyan-500 text-slate-900 rounded-lg hover:bg-cyan-400 transition-colors"
            data-testid="add-achievement-btn"
          >
            <Plus size={18} />
            {t('admin.achievements.addNew')}
          </button>

          {/* Achievement Form Modal */}
          {showAchievementForm && (
            <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
              <div className="bg-slate-800 rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                <div className="sticky top-0 bg-slate-800 p-6 border-b border-slate-700 flex justify-between items-center">
                  <h2 className="text-xl font-bold text-white">
                    {editingAchievement ? t('admin.achievements.editAchievement') : t('admin.achievements.addNew')}
                  </h2>
                  <button onClick={resetAchievementForm} className="text-gray-400 hover:text-white">
                    <X size={24} />
                  </button>
                </div>

                <form onSubmit={handleAchievementSubmit} className="p-6 space-y-6">
                  {/* Language Tabs */}
                  <div className="flex gap-2 border-b border-slate-700 pb-3">
                    <button
                      type="button"
                      onClick={() => setLangTab('en')}
                      className={`px-4 py-2 rounded-t-lg ${langTab === 'en' ? 'bg-cyan-500/20 text-cyan-400' : 'text-gray-400'}`}
                    >
                      {t('admin.common.english')}
                    </button>
                    <button
                      type="button"
                      onClick={() => setLangTab('tr')}
                      className={`px-4 py-2 rounded-t-lg ${langTab === 'tr' ? 'bg-cyan-500/20 text-cyan-400' : 'text-gray-400'}`}
                    >
                      {t('admin.common.turkish')}
                    </button>
                  </div>

                  {/* Title */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      {t('admin.achievements.form.title')} *
                    </label>
                    {langTab === 'en' ? (
                      <input
                        type="text"
                        value={achievementForm.title}
                        onChange={(e) => setAchievementForm({...achievementForm, title: e.target.value})}
                        className="w-full px-4 py-3 bg-slate-900 border border-slate-700 rounded-lg text-white focus:border-cyan-500 focus:outline-none"
                        placeholder={t('admin.achievements.form.titlePlaceholder')}
                        required
                      />
                    ) : (
                      <>
                        <input
                          type="text"
                          value={achievementForm.title_tr}
                          onChange={(e) => setAchievementForm({...achievementForm, title_tr: e.target.value})}
                          className="w-full px-4 py-3 bg-slate-900 border border-slate-700 rounded-lg text-white focus:border-cyan-500 focus:outline-none"
                          placeholder={t('admin.achievements.form.titlePlaceholder')}
                        />
                        {achievementForm.title && (
                          <p className="text-xs text-gray-500 mt-1">EN: {achievementForm.title}</p>
                        )}
                      </>
                    )}
                  </div>

                  {/* Description */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      {t('admin.achievements.form.description')}
                    </label>
                    {langTab === 'en' ? (
                      <textarea
                        value={achievementForm.description}
                        onChange={(e) => setAchievementForm({...achievementForm, description: e.target.value})}
                        rows={3}
                        className="w-full px-4 py-3 bg-slate-900 border border-slate-700 rounded-lg text-white focus:border-cyan-500 focus:outline-none resize-none"
                        placeholder={t('admin.achievements.form.descriptionPlaceholder')}
                      />
                    ) : (
                      <>
                        <textarea
                          value={achievementForm.description_tr}
                          onChange={(e) => setAchievementForm({...achievementForm, description_tr: e.target.value})}
                          rows={3}
                          className="w-full px-4 py-3 bg-slate-900 border border-slate-700 rounded-lg text-white focus:border-cyan-500 focus:outline-none resize-none"
                          placeholder={t('admin.achievements.form.descriptionPlaceholder')}
                        />
                        {achievementForm.description && (
                          <p className="text-xs text-gray-500 mt-1">EN: {achievementForm.description.substring(0, 100)}...</p>
                        )}
                      </>
                    )}
                  </div>

                  {/* Year, Category Row */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        {t('admin.achievements.form.year')} *
                      </label>
                      <input
                        type="number"
                        value={achievementForm.year}
                        onChange={(e) => setAchievementForm({...achievementForm, year: parseInt(e.target.value)})}
                        className="w-full px-4 py-3 bg-slate-900 border border-slate-700 rounded-lg text-white focus:border-cyan-500 focus:outline-none"
                        min="1990"
                        max="2100"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        {t('admin.achievements.form.category')} *
                      </label>
                      <select
                        value={achievementForm.category_id}
                        onChange={(e) => setAchievementForm({...achievementForm, category_id: e.target.value})}
                        className="w-full px-4 py-3 bg-slate-900 border border-slate-700 rounded-lg text-white focus:border-cyan-500 focus:outline-none"
                        required
                      >
                        <option value="">{t('admin.achievements.form.selectCategory')}</option>
                        {categories.map((cat) => (
                          <option key={cat.id} value={cat.id}>
                            {language === 'tr' && cat.name_tr ? cat.name_tr : cat.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Ranking */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      {t('admin.achievements.form.ranking')}
                    </label>
                    {langTab === 'en' ? (
                      <input
                        type="text"
                        value={achievementForm.ranking}
                        onChange={(e) => setAchievementForm({...achievementForm, ranking: e.target.value})}
                        className="w-full px-4 py-3 bg-slate-900 border border-slate-700 rounded-lg text-white focus:border-cyan-500 focus:outline-none"
                        placeholder={t('admin.achievements.form.rankingPlaceholder')}
                      />
                    ) : (
                      <>
                        <input
                          type="text"
                          value={achievementForm.ranking_tr}
                          onChange={(e) => setAchievementForm({...achievementForm, ranking_tr: e.target.value})}
                          className="w-full px-4 py-3 bg-slate-900 border border-slate-700 rounded-lg text-white focus:border-cyan-500 focus:outline-none"
                          placeholder={t('admin.achievements.form.rankingPlaceholder')}
                        />
                        {achievementForm.ranking && (
                          <p className="text-xs text-gray-500 mt-1">EN: {achievementForm.ranking}</p>
                        )}
                      </>
                    )}
                  </div>

                  {/* Location */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      {t('admin.achievements.form.location')}
                    </label>
                    {langTab === 'en' ? (
                      <input
                        type="text"
                        value={achievementForm.location}
                        onChange={(e) => setAchievementForm({...achievementForm, location: e.target.value})}
                        className="w-full px-4 py-3 bg-slate-900 border border-slate-700 rounded-lg text-white focus:border-cyan-500 focus:outline-none"
                        placeholder={t('admin.achievements.form.locationPlaceholder')}
                      />
                    ) : (
                      <>
                        <input
                          type="text"
                          value={achievementForm.location_tr}
                          onChange={(e) => setAchievementForm({...achievementForm, location_tr: e.target.value})}
                          className="w-full px-4 py-3 bg-slate-900 border border-slate-700 rounded-lg text-white focus:border-cyan-500 focus:outline-none"
                          placeholder={t('admin.achievements.form.locationPlaceholder')}
                        />
                        {achievementForm.location && (
                          <p className="text-xs text-gray-500 mt-1">EN: {achievementForm.location}</p>
                        )}
                      </>
                    )}
                  </div>

                  {/* Image */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      {t('admin.achievements.form.image')}
                    </label>
                    <ImageUploader
                      value={achievementForm.image}
                      onChange={(url) => setAchievementForm({...achievementForm, image: url})}
                    />
                  </div>

                  {/* Featured Toggle */}
                  <div className="flex items-center gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={achievementForm.featured}
                        onChange={(e) => setAchievementForm({...achievementForm, featured: e.target.checked})}
                        className="w-5 h-5 rounded border-slate-700 bg-slate-900 text-cyan-500 focus:ring-cyan-500"
                      />
                      <span className="text-gray-300 flex items-center gap-2">
                        <Star size={16} className={achievementForm.featured ? 'text-yellow-500' : 'text-gray-500'} />
                        {t('admin.achievements.form.featured')}
                      </span>
                    </label>
                  </div>

                  {/* Featured Color (only if featured) */}
                  {achievementForm.featured && (
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        {t('admin.achievements.form.featuredColor')}
                      </label>
                      <div className="flex gap-3">
                        {colorOptions.map((color) => (
                          <button
                            key={color.value}
                            type="button"
                            onClick={() => setAchievementForm({...achievementForm, featured_color: color.value})}
                            className={`w-10 h-10 rounded-lg border-2 transition-all ${
                              achievementForm.featured_color === color.value
                                ? 'border-white scale-110'
                                : 'border-transparent'
                            }`}
                            style={{ backgroundColor: color.color }}
                            title={color.label}
                          />
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Submit Buttons */}
                  <div className="flex gap-3 pt-4">
                    <button
                      type="submit"
                      disabled={saving}
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-cyan-500 text-slate-900 rounded-lg hover:bg-cyan-400 transition-colors disabled:opacity-50"
                    >
                      {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save size={18} />}
                      {editingAchievement ? t('admin.common.edit') : t('admin.common.create')}
                    </button>
                    <button
                      type="button"
                      onClick={resetAchievementForm}
                      className="px-6 py-3 bg-slate-700 text-white rounded-lg hover:bg-slate-600 transition-colors"
                    >
                      {t('admin.common.cancel')}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Achievements List */}
          <div className="bg-slate-800/50 rounded-xl overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-700">
                  <th className="text-left py-4 px-6 text-gray-400 font-medium">{t('admin.achievements.form.title')}</th>
                  <th className="text-left py-4 px-6 text-gray-400 font-medium">{t('admin.achievements.form.year')}</th>
                  <th className="text-left py-4 px-6 text-gray-400 font-medium">{t('admin.achievements.form.category')}</th>
                  <th className="text-left py-4 px-6 text-gray-400 font-medium">{t('admin.achievements.form.ranking')}</th>
                  <th className="text-right py-4 px-6 text-gray-400 font-medium"></th>
                </tr>
              </thead>
              <tbody>
                {achievements.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="py-12 text-center text-gray-500">
                      {t('admin.achievements.noAchievements')}
                    </td>
                  </tr>
                ) : (
                  achievements.map((achievement) => (
                    <tr key={achievement.id} className="border-b border-slate-700/50 hover:bg-slate-700/30">
                      <td className="py-4 px-6">
                        <div className="flex items-center gap-3">
                          {achievement.featured && (
                            <Star size={16} className="text-yellow-500" />
                          )}
                          <div>
                            <p className="text-white font-medium">
                              {language === 'tr' && achievement.title_tr ? achievement.title_tr : achievement.title}
                            </p>
                            {achievement.title_tr && language === 'en' && (
                              <p className="text-xs text-gray-500">TR: {achievement.title_tr}</p>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-6 text-gray-300">{achievement.year}</td>
                      <td className="py-4 px-6 text-gray-300">{getCategoryName(achievement.category_id)}</td>
                      <td className="py-4 px-6 text-gray-300">
                        {language === 'tr' && achievement.ranking_tr ? achievement.ranking_tr : achievement.ranking || '-'}
                      </td>
                      <td className="py-4 px-6">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => handleEditAchievement(achievement)}
                            className="p-2 text-gray-400 hover:text-cyan-400 transition-colors"
                            title={t('admin.common.edit')}
                          >
                            <Pencil size={18} />
                          </button>
                          <button
                            onClick={() => handleDeleteAchievement(achievement.id)}
                            className="p-2 text-gray-400 hover:text-red-400 transition-colors"
                            title={t('admin.common.delete')}
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Categories Tab */}
      {activeTab === 'categories' && (
        <div className="space-y-6">
          {/* Add Button */}
          <button
            onClick={() => {
              resetCategoryForm();
              setShowCategoryForm(true);
            }}
            className="flex items-center gap-2 px-4 py-2 bg-cyan-500 text-slate-900 rounded-lg hover:bg-cyan-400 transition-colors"
            data-testid="add-category-btn"
          >
            <Plus size={18} />
            {t('admin.achievements.categories.addNew')}
          </button>

          {/* Category Form Modal */}
          {showCategoryForm && (
            <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
              <div className="bg-slate-800 rounded-xl w-full max-w-md">
                <div className="p-6 border-b border-slate-700 flex justify-between items-center">
                  <h2 className="text-xl font-bold text-white">
                    {editingCategory ? t('admin.achievements.categories.editCategory') : t('admin.achievements.categories.addNew')}
                  </h2>
                  <button onClick={resetCategoryForm} className="text-gray-400 hover:text-white">
                    <X size={24} />
                  </button>
                </div>

                <form onSubmit={handleCategorySubmit} className="p-6 space-y-6">
                  {/* Language Tabs */}
                  <div className="flex gap-2 border-b border-slate-700 pb-3">
                    <button
                      type="button"
                      onClick={() => setLangTab('en')}
                      className={`px-4 py-2 rounded-t-lg ${langTab === 'en' ? 'bg-cyan-500/20 text-cyan-400' : 'text-gray-400'}`}
                    >
                      {t('admin.common.english')}
                    </button>
                    <button
                      type="button"
                      onClick={() => setLangTab('tr')}
                      className={`px-4 py-2 rounded-t-lg ${langTab === 'tr' ? 'bg-cyan-500/20 text-cyan-400' : 'text-gray-400'}`}
                    >
                      {t('admin.common.turkish')}
                    </button>
                  </div>

                  {/* Name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      {t('admin.achievements.categories.form.name')} *
                    </label>
                    {langTab === 'en' ? (
                      <input
                        type="text"
                        value={categoryForm.name}
                        onChange={(e) => setCategoryForm({...categoryForm, name: e.target.value})}
                        className="w-full px-4 py-3 bg-slate-900 border border-slate-700 rounded-lg text-white focus:border-cyan-500 focus:outline-none"
                        placeholder={t('admin.achievements.categories.form.namePlaceholder')}
                        required
                      />
                    ) : (
                      <>
                        <input
                          type="text"
                          value={categoryForm.name_tr}
                          onChange={(e) => setCategoryForm({...categoryForm, name_tr: e.target.value})}
                          className="w-full px-4 py-3 bg-slate-900 border border-slate-700 rounded-lg text-white focus:border-cyan-500 focus:outline-none"
                          placeholder={t('admin.achievements.categories.form.namePlaceholder')}
                        />
                        {categoryForm.name && (
                          <p className="text-xs text-gray-500 mt-1">EN: {categoryForm.name}</p>
                        )}
                      </>
                    )}
                  </div>

                  {/* Icon */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      {t('admin.achievements.categories.form.icon')}
                    </label>
                    <select
                      value={categoryForm.icon}
                      onChange={(e) => setCategoryForm({...categoryForm, icon: e.target.value})}
                      className="w-full px-4 py-3 bg-slate-900 border border-slate-700 rounded-lg text-white focus:border-cyan-500 focus:outline-none"
                    >
                      {iconOptions.map((icon) => (
                        <option key={icon.value} value={icon.value}>{icon.label}</option>
                      ))}
                    </select>
                  </div>

                  {/* Order */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      {language === 'tr' ? 'Sıra' : 'Order'}
                    </label>
                    <input
                      type="number"
                      value={categoryForm.order}
                      onChange={(e) => setCategoryForm({...categoryForm, order: parseInt(e.target.value)})}
                      className="w-full px-4 py-3 bg-slate-900 border border-slate-700 rounded-lg text-white focus:border-cyan-500 focus:outline-none"
                      min="0"
                    />
                  </div>

                  {/* Submit Buttons */}
                  <div className="flex gap-3 pt-4">
                    <button
                      type="submit"
                      disabled={saving}
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-cyan-500 text-slate-900 rounded-lg hover:bg-cyan-400 transition-colors disabled:opacity-50"
                    >
                      {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save size={18} />}
                      {editingCategory ? t('admin.common.edit') : t('admin.common.create')}
                    </button>
                    <button
                      type="button"
                      onClick={resetCategoryForm}
                      className="px-6 py-3 bg-slate-700 text-white rounded-lg hover:bg-slate-600 transition-colors"
                    >
                      {t('admin.common.cancel')}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Categories List */}
          <div className="bg-slate-800/50 rounded-xl overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-700">
                  <th className="text-left py-4 px-6 text-gray-400 font-medium">{t('admin.achievements.categories.form.name')}</th>
                  <th className="text-left py-4 px-6 text-gray-400 font-medium">{t('admin.achievements.categories.form.icon')}</th>
                  <th className="text-left py-4 px-6 text-gray-400 font-medium">{language === 'tr' ? 'Başarı Sayısı' : 'Achievements'}</th>
                  <th className="text-right py-4 px-6 text-gray-400 font-medium"></th>
                </tr>
              </thead>
              <tbody>
                {categories.length === 0 ? (
                  <tr>
                    <td colSpan="4" className="py-12 text-center text-gray-500">
                      {t('admin.achievements.categories.noCategories')}
                    </td>
                  </tr>
                ) : (
                  categories.map((category) => {
                    const achievementCount = achievements.filter(a => a.category_id === category.id).length;
                    return (
                      <tr key={category.id} className="border-b border-slate-700/50 hover:bg-slate-700/30">
                        <td className="py-4 px-6">
                          <div>
                            <p className="text-white font-medium">
                              {language === 'tr' && category.name_tr ? category.name_tr : category.name}
                            </p>
                            {category.name_tr && language === 'en' && (
                              <p className="text-xs text-gray-500">TR: {category.name_tr}</p>
                            )}
                          </div>
                        </td>
                        <td className="py-4 px-6 text-gray-300 capitalize">{category.icon}</td>
                        <td className="py-4 px-6 text-gray-300">{achievementCount}</td>
                        <td className="py-4 px-6">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => handleEditCategory(category)}
                              className="p-2 text-gray-400 hover:text-cyan-400 transition-colors"
                              title={t('admin.common.edit')}
                            >
                              <Pencil size={18} />
                            </button>
                            <button
                              onClick={() => handleDeleteCategory(category.id)}
                              className="p-2 text-gray-400 hover:text-red-400 transition-colors"
                              title={t('admin.common.delete')}
                              disabled={achievementCount > 0}
                            >
                              <Trash2 size={18} className={achievementCount > 0 ? 'opacity-30' : ''} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminAchievements;
